/**
 * Service to handle HAL responses / requests
 */

import { Api } from 'domain/core';
import { Page } from 'domain/models';
import { Identifiable } from 'domain/models/base.model';
import { PagedQueryParams } from 'domain/utils/query.utils';
import { useCallback, useEffect, useState } from 'react';
import useFetch from '../../services/auth/fetch.service';
import useSWR from 'swr';
import { EntitiesRels } from 'domain/models/api.models';

export type HalService = ReturnType<typeof useHal>;

export type UseHalType<T> = {
	find: (query?: PagedQueryParams) => Promise<Page<T>>;
	findOne: (id: string) => Promise<T>;
	create: (entity: T) => Promise<T>;
	update: (entity: T) => Promise<T>;
	remove: (entity: T) => Promise<T>;
	findNested: (url: string) => Promise<T>;
};

export const useHal = <T extends Identifiable>(
	resource: string
): UseHalType<T> => {
	const authFetch = useFetch();

	const find = async (query?: PagedQueryParams): Promise<Page<T>> => {
		const response = await authFetch(
			`${Api.baseUrl}${Api.hal.base}/${resource}${
				query ? '?' + query.getQueryParams() : ''
			}`
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}
		const data = await response.json();
		// The response is a HAL response, so we need to extract the data from it
		return data;
	};

	const findOne = useCallback(
		async (id: string): Promise<T> => {
			const response = await authFetch(
				`${Api.baseUrl}${Api.hal.base}/${resource}/${id}`
			);
			if (!response.ok) {
				throw new Error(response.statusText);
			}
			const data = await response.json();
			// The response is a HAL response, so we need to extract the nested data from it
			return data;
		},
		[resource]
	);

	const findNested = async (url: string): Promise<T> => {
		const response = await authFetch(`${url}`);
		if (!response.ok) {
			throw new Error(response.statusText);
		}
		const data = await response.json();
		// The response is a HAL response, so we need to extract the nested data from it
		return data;
	};

	const create = async (entity: T): Promise<T> => {
		let response: Response | undefined;
		try {
			response = await authFetch(`${Api.baseUrl}${Api.hal.base}/${resource}`, {
				method: 'POST',
				body: JSON.stringify(entity),
			});
			if (!response.ok) {
				throw new Error(response.statusText);
			}
			const data = await response.json();
			return data;
		} catch (error) {
			// Check status >= 200 && status < 300
			if (response && response.status >= 200 && response.status < 300) {
				return entity;
			}

			throw error;
		}
		return entity;
	};

	const update = async (entity: T): Promise<T> => {
		const response = await authFetch(
			`${Api.baseUrl}${Api.hal.base}/${resource}/${entity['id']}`,
			{
				method: 'PUT',
				body: JSON.stringify(entity),
			}
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}

		// If 204, no content is returned
		if (response.status === 204) {
			return entity;
		}
		const data = await response.json();
		return data;
	};

	const remove = async (entity: T): Promise<T> => {
		if (!entity['id']) {
			return entity;
		}

		// Check if entity has .deleted property
		if (!('deleted' in entity)) {
			return entity;
		}

		// Set deleted to true
		entity['deleted'] = true;

		// Update entity
		const response = await update(entity);
		return response;
	};

	return {
		find,
		findOne,
		create,
		update,
		remove,
		findNested,
	};
};

export const useRelEntities = <T extends Identifiable>(rel: string): [T[]] => {
	const { find } = useHal<T>(rel);

	const [entities, setEntities] = useState<T[]>([]);

	const load = async (): Promise<T[]> => {
		const response = await find();
		return response.content;
	};

	useEffect(() => {
		load()
			.then((data) => {
				setEntities(data);
			})
			.catch((err) => {
				console.error(err);
			});
	}, []);

	return [entities];
};

const NO_ENTITY = { entity: false, rel: '' };

export const useIsEntity = () => {
	const fetch = useFetch();

	const { data, isLoading, error } = useSWR<EntitiesRels>(
		`${Api.baseUrl}${Api.schemas.managedEntities}`,
		(u: RequestInfo) => fetch(u).then((r) => r.json())
	);

	const isEntityFn = useCallback(
		(
			id: string | null | undefined,
			schema: any | null | undefined
		): { entity: boolean; rel: string } => {
			if (!id && !schema) {
				return NO_ENTITY;
			}

			if (!id) {
				// Check for the schema.type
				const partId = schema?.id || schema?.$ref;
				if (schema.type === 'object' && partId) {
					id = partId;
				}
			}

			if (!id) {
				return NO_ENTITY;
			}

			const entity = data?.find((e) => e.id === id);
			if (!entity) {
				return NO_ENTITY;
			}

			return { entity: true, rel: entity.url.rel };
		},
		[data]
	);

	return isEntityFn;
};
